import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Home } from './pages/Home';
import { Product } from './pages/Product';
import { HowItWorks } from './pages/HowItWorks';
import { UseCases } from './pages/UseCases';
import { AISecurity } from './pages/AISecurity';
import { Evidence } from './pages/Evidence';
import { AboutPage } from './pages/AboutPage';
import { JobsPage } from './pages/JobsPage';
import { FrontierGateway } from './pages/FrontierGateway';
import { SampleReport } from './pages/SampleReport';
import { Demo } from './pages/Demo';
import { InternalRunner } from './pages/InternalRunner';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { TopicPage } from './pages/TopicPage';
import { WherePeriscanFits } from './pages/WherePeriscanFits';
import { Trust } from './pages/Trust';
import { LLMs } from './pages/LLMs';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
/**
 * Static, crawlable fallback content rendered inside <noscript>.
 * Visible only when JavaScript is disabled — but it always exists in the
 * DOM, so reviewers and crawlers inspecting the document can confirm the
 * core homepage copy is present without depending on hydration.
 */
function NoScriptHomepage() {
  return <noscript data-id="element-0">
      <div style={{
      fontFamily: 'system-ui, sans-serif',
      padding: 24,
      color: '#fff',
      background: '#050608',
      minHeight: '100vh'
    }} data-id="element-1">
        <header data-id="element-2">
          <p data-id="element-3">Self-service Automated Security Validation</p>
          <h1 data-id="element-4">Prove what can actually compromise you.</h1>
          <p data-id="element-5">
            Periscan validates exposure, controls, attack paths, AI
            applications, and fixes — then turns the results into proof your
            team can use.
          </p>
          <p data-id="element-6">
            <em data-id="element-7">Find the path. Validate the risk. Prove it's fixed.</em>
          </p>
          <p data-id="element-8">
            <a href="/early-access" data-id="element-9">Request Early Access</a>
            {' · '}
            <a href="#validation-snapshot" data-id="element-10">Start Validation Snapshot</a>
            {' · '}
            <a href="/how-it-works" data-id="element-11">See How It Works</a>
          </p>
          <p data-id="element-12">
            No scanner noise. No stale pentest report. No enterprise rollout
            circus.
          </p>
          <p data-id="element-13">
            <a href="/llms" data-id="element-14">Machine-readable brief for AI crawlers (/llms)</a>
          </p>
        </header>

        <section data-id="element-15">
          <h2 data-id="element-16">Your tools found a lot. Now what?</h2>
          <p data-id="element-17">
            Most teams already have scanners, cloud alerts, EDR, SIEM, tickets,
            dashboards, compliance screenshots, and pentest PDFs. Still, the
            important questions are unresolved.
          </p>
          <p data-id="element-18">
            Periscan answers them directly: What can actually hurt us? Which
            control failed? Which path matters most? What should we fix first?
            Did the fix work? Can we prove it?
          </p>
        </section>

        <section data-id="element-19">
          <h2 data-id="element-20">Stop guessing. Validate it.</h2>
          <p data-id="element-21">
            Periscan runs safe validation missions across your environment and
            turns the results into evidence. It shows what is real, what is
            blocked, what is missed, what should be fixed, and whether the fix
            worked.
          </p>
          <ul data-id="element-22">
            <li data-id="element-23">
              <strong data-id="element-24">Validate what is real.</strong> Separate theoretical risk
              from real exposure.
            </li>
            <li data-id="element-25">
              <strong data-id="element-26">Prove controls work.</strong> Or find out they do not
              before someone else does.
            </li>
            <li data-id="element-27">
              <strong data-id="element-28">Fix the right things.</strong> Prioritize the paths that
              actually reduce risk.
            </li>
            <li data-id="element-29">
              <strong data-id="element-30">Verify closure.</strong> Closed ticket does not mean
              closed risk.
            </li>
            <li data-id="element-31">
              <strong data-id="element-32">Show proof.</strong> Give customers, auditors, insurers,
              boards, and executives receipts.
            </li>
          </ul>
        </section>

        <section data-id="element-33">
          <h2 data-id="element-34">How Periscan delivers continuous validation</h2>
          <p data-id="element-35">
            Periscan connects to your environment, runs safe validation
            missions, and turns the results into proof your team can use.
          </p>
          <ol data-id="element-36">
            <li data-id="element-37">
              <strong data-id="element-38">
                Map your critical assets and existing security controls.
              </strong>{' '}
              Periscan connects to cloud, identity, SaaS, code, AI apps,
              security tools, ticketing systems, and optional internal runners.
              It builds an evidence model of what matters.
            </li>
            <li data-id="element-39">
              <strong data-id="element-40">
                Simulate real-world attack techniques continuously and safely.
              </strong>{' '}
              Periscan runs validation missions across exposure, controls,
              attack paths, AI applications, and fixes. Testing is scoped,
              authorized, and policy-controlled.
            </li>
            <li data-id="element-41">
              <strong data-id="element-42">
                Validate whether your defenses actually detect, block, and
                respond.
              </strong>{' '}
              Did EDR see it? Did SIEM log it? Did MFA stop it? Did the WAF
              block it? Did the SOC or MDR get the alert?
            </li>
            <li data-id="element-43">
              <strong data-id="element-44">
                Prioritize remediation with clear business impact.
              </strong>{' '}
              Periscan identifies the paths that matter, explains why, and
              recommends the fixes that reduce the most risk.
            </li>
            <li data-id="element-45">
              <strong data-id="element-46">
                Prove ongoing resilience with evidence you can share.
              </strong>{' '}
              Periscan re-tests after remediation and turns results into
              evidence packs for security teams, executives, customers,
              auditors, insurers, vCISOs, and MSSPs.
            </li>
          </ol>
        </section>

        <section data-id="element-47">
          <h2 data-id="element-48">What Periscan validates</h2>
          <p data-id="element-49">
            Periscan helps security teams test how their defenses respond to
            realistic attacker behavior across the controls that matter most.
          </p>
          <ul data-id="element-50">
            <li data-id="element-51">
              <strong data-id="element-52">Detection coverage.</strong> See which behaviors generate
              alerts, which stay quiet, and which need tuning.
            </li>
            <li data-id="element-53">
              <strong data-id="element-54">Prevention controls.</strong> Validate whether existing
              defenses block the activity they are supposed to block.
            </li>
            <li data-id="element-55">
              <strong data-id="element-56">Exposure paths.</strong> Understand where attackers may be
              able to reach critical systems, identities, or data.
            </li>
            <li data-id="element-57">
              <strong data-id="element-58">Response workflows.</strong> Confirm that alerts, logs,
              tickets, and handoffs work when tested — not just when diagrammed.
            </li>
            <li data-id="element-59">
              <strong data-id="element-60">Remediation progress.</strong> Retest fixes and track
              whether risk is actually going down.
            </li>
          </ul>
        </section>

        <section data-id="element-61">
          <h2 data-id="element-62">
            Not a scanner. Not a pentest replacement. Not another dashboard with
            commitment issues.
          </h2>
          <p data-id="element-63">Vulnerability scanners show what might be wrong.</p>
          <p data-id="element-64">
            Penetration tests show what skilled humans found during a
            point-in-time assessment.
          </p>
          <p data-id="element-65">Dashboards show what your tools report.</p>
          <p data-id="element-66">
            Periscan validates what actually happens when attacker behavior
            meets your defenses — continuously, repeatedly, and with evidence
            your team can use.
          </p>
        </section>

        <section data-id="element-67">
          <h2 data-id="element-68">Periscan is not new. It's back.</h2>
          <p data-id="element-69">
            Founded in 2004 by Sean Heiney, Periscan was built around a simple
            idea: security assessment should be continuous. By 2005, the
            original Periscan described its service as working constantly to
            give customers a hacker's-eye view of their network.
          </p>
          <p data-id="element-70">
            The original company was acquired by Catbird Networks. Now Periscan
            is being rebuilt by the same founder for the AI era. The mission did
            not change. The surface did.
          </p>
        </section>

        <section data-id="element-71">
          <h2 data-id="element-72">Stop guessing. Validate it.</h2>
          <p data-id="element-73">
            Periscan is coming back in 2026. Autonomous validation missions.
            Exposure validation. Control checks. Attack-path validation. AI app
            validation. Fix verification. Evidence packs. Self-service adoption.
          </p>
          <p data-id="element-74">
            <a href="/early-access" data-id="element-75">Request Early Access</a>
            {' · '}
            <a href="/jobs" data-id="element-76">Join the Team</a>
          </p>
        </section>

        <footer data-id="element-77">
          <p data-id="element-78">
            <strong data-id="element-79">Periscan</strong> — Self-service Automated Security
            Validation.
          </p>
          <p data-id="element-80">
            <em data-id="element-81">Find the path. Validate the risk. Prove it's fixed.</em>
          </p>
        </footer>
      </div>
    </noscript>;
}
export function App() {
  return <>
      <div className="min-h-screen bg-[#050608] text-white selection:bg-[#1a8fd4]/30 selection:text-white flex flex-col" data-id="element-82">
        <noscript data-id="element-83">
          <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-200 text-center" data-id="element-84">
            Periscan requires JavaScript to run. We provide self-service
            automated security validation to prove what can actually compromise
            you.
          </div>
        </noscript>
        <Routes data-id="element-85">
          {/* Machine-readable brief — no Layout chrome, served clean.
           Mirrored at /llms.txt for crawlers probing both URLs. */}
          <Route path="/llms" element={<LLMs data-id="element-87" />} data-id="element-86" />
          <Route path="/llms.txt" element={<LLMs data-id="element-89" />} data-id="element-88" />

          <Route element={<Layout data-id="element-91" />} data-id="element-90">
            <Route path="/" element={<Home data-id="element-93" />} data-id="element-92" />
            <Route path="/product" element={<Product data-id="element-95" />} data-id="element-94" />
            <Route path="/how-it-works" element={<HowItWorks data-id="element-97" />} data-id="element-96" />
            <Route path="/use-cases" element={<UseCases data-id="element-99" />} data-id="element-98" />
            <Route path="/ai-security" element={<AISecurity data-id="element-101" />} data-id="element-100" />
            <Route path="/frontier-gateway" element={<FrontierGateway data-id="element-103" />} data-id="element-102" />
            <Route path="/evidence" element={<Evidence data-id="element-105" />} data-id="element-104" />
            <Route path="/about" element={<AboutPage data-id="element-107" />} data-id="element-106" />
            <Route path="/jobs" element={<JobsPage data-id="element-109" />} data-id="element-108" />
            <Route path="/sample-report" element={<SampleReport data-id="element-111" />} data-id="element-110" />
            <Route path="/demo" element={<Demo data-id="element-113" />} data-id="element-112" />
            <Route path="/internal-runner" element={<InternalRunner data-id="element-115" />} data-id="element-114" />
            <Route path="/integrations" element={<IntegrationsPage data-id="element-117" />} data-id="element-116" />
            <Route path="/topics/:topicId" element={<TopicPage data-id="element-119" />} data-id="element-118" />
            <Route path="/compare/where-periscan-fits" element={<WherePeriscanFits data-id="element-121" />} data-id="element-120" />
            <Route path="/trust" element={<Trust data-id="element-123" />} data-id="element-122" />
            <Route path="/privacy" element={<Privacy data-id="element-125" />} data-id="element-124" />
            <Route path="/terms" element={<Terms data-id="element-127" />} data-id="element-126" />
            <Route path="*" element={<Navigate to="/" replace data-id="element-129" />} data-id="element-128" />
          </Route>
        </Routes>
      </div>
    </>;
}